/**
 * Registration — /register route.
 *
 * Step in the sign-up approval gate (see RequireApproved in App.tsx). After
 * OIDC sign-in a brand-new user (approval_status='pending' && !approval_submitted)
 * is routed here to provide their details before a platform admin reviews them.
 * On submit we POST /v1/platform/auth/registration, refresh the platform
 * identity (so approval_submitted flips true → the gate sends them onward),
 * and navigate to /pending-approval.
 *
 * Email is read-only — the server uses the verified session email, so we never
 * send it in the body (frozen backend contract). Every other field is mandatory
 * with inline validation that blocks submit.
 */

import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";

import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api-client";
import { ApiClientError } from "@/lib/api-client";
import { useToast } from "@/lib/ui/toast";
import { BrandMark } from "@/lib/ui/brand-mark";
import { useTenantTheme } from "@/lib/tenant-theme";
import { Button } from "@/lib/ui/button";
import { Badge } from "@/lib/ui/badge";
import { Input } from "@/lib/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/lib/ui/card";

type FieldKey = "name" | "company" | "position" | "phone";

interface FormState {
  name: string;
  company: string;
  position: string;
  phone: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  company: "",
  position: "",
  phone: "",
};

const FIELD_LABELS: Record<FieldKey, string> = {
  name: "Full name",
  company: "Company",
  position: "Position",
  phone: "Phone",
};

export function Register() {
  const navigate = useNavigate();
  const { user, refreshIdentity } = useAuth();
  const tenant = useTenantTheme();
  const toast = useToast();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [touched, setTouched] = useState<Record<FieldKey, boolean>>({
    name: false,
    company: false,
    position: false,
    phone: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (key: FieldKey, value: string) =>
    setForm((s) => ({ ...s, [key]: value }));
  const markTouched = (key: FieldKey) =>
    setTouched((t) => ({ ...t, [key]: true }));

  // A field is invalid only when blank. Show the inline error once the field
  // has been touched (or after a submit attempt marks all touched).
  const errorFor = (key: FieldKey): string | null =>
    form[key].trim().length === 0 ? `${FIELD_LABELS[key]} is required.` : null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;

    // Reveal every inline error and block submit if anything is blank.
    setTouched({ name: true, company: true, position: true, phone: true });
    const hasBlank = (Object.keys(FIELD_LABELS) as FieldKey[]).some(
      (k) => form[k].trim().length === 0,
    );
    if (hasBlank) return;

    setSubmitting(true);
    try {
      // Email is deliberately omitted — the backend uses the verified session
      // email (frozen contract).
      await api.post("/v1/platform/auth/registration", {
        name: form.name.trim(),
        company: form.company.trim(),
        position: form.position.trim(),
        phone: form.phone.trim(),
      });
      await refreshIdentity();
      navigate("/pending-approval", { replace: true });
    } catch (e) {
      toast.push("error", registrationErrorMessage(e));
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface px-4 py-10">
      <div className="mb-6 flex items-center gap-2">
        <BrandMark />
        <span className="text-base font-semibold text-on-surface">
          {tenant.productName}
        </span>
      </div>

      <Card className="w-full max-w-lg">
        <CardHeader>
          <Badge variant="info" className="w-fit uppercase tracking-wide">
            One more step
          </Badge>
          <CardTitle className="text-2xl">Request access</CardTitle>
          <CardDescription>
            Tell us a little about you. A platform administrator reviews every
            new request before your workspace is activated — you'll get in as
            soon as you're approved.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
            <LabelledField label={FIELD_LABELS.name} error={touched.name ? errorFor("name") : null}>
              <Input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                onBlur={() => markTouched("name")}
                placeholder="Jane Doe"
                autoComplete="name"
                autoFocus
                disabled={submitting}
                aria-invalid={touched.name && errorFor("name") !== null}
              />
            </LabelledField>

            <LabelledField
              label={FIELD_LABELS.company}
              error={touched.company ? errorFor("company") : null}
            >
              <Input
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                onBlur={() => markTouched("company")}
                placeholder="Acme Castings Pvt. Ltd."
                autoComplete="organization"
                disabled={submitting}
                aria-invalid={touched.company && errorFor("company") !== null}
              />
            </LabelledField>

            <LabelledField
              label={FIELD_LABELS.position}
              error={touched.position ? errorFor("position") : null}
            >
              <Input
                value={form.position}
                onChange={(e) => set("position", e.target.value)}
                onBlur={() => markTouched("position")}
                placeholder="Plant Manager"
                autoComplete="organization-title"
                disabled={submitting}
                aria-invalid={touched.position && errorFor("position") !== null}
              />
            </LabelledField>

            <LabelledField label="Email">
              <Input
                value={user?.email ?? ""}
                disabled
                readOnly
                type="email"
                autoComplete="email"
              />
              <p className="mt-1 text-[11px] text-on-surface-muted">
                Taken from your verified sign-in — this can't be changed here.
              </p>
            </LabelledField>

            <LabelledField label={FIELD_LABELS.phone} error={touched.phone ? errorFor("phone") : null}>
              <Input
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                onBlur={() => markTouched("phone")}
                placeholder="+91 98765 43210"
                type="tel"
                autoComplete="tel"
                disabled={submitting}
                aria-invalid={touched.phone && errorFor("phone") !== null}
              />
            </LabelledField>

            <div className="flex items-center gap-2 rounded-md border border-border bg-surface-elevated/40 p-3 text-xs text-on-surface-muted">
              <ShieldCheck className="size-4 shrink-0 text-brand" />
              <span>
                We only use these details to review and set up your account.
              </span>
            </div>

            <Button type="submit" size="lg" disabled={submitting} className="mt-1">
              {submitting && <Loader2 className="mr-1.5 size-4 animate-spin" />}
              {submitting ? "Submitting…" : "Submit request"}
              {!submitting && <ArrowRight className="ml-1.5 size-4" />}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function LabelledField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        {label}
      </span>
      {children}
      {error && <span className="text-[11px] text-danger">{error}</span>}
    </label>
  );
}

/** Map a registration API failure to a short, friendly toast message. */
function registrationErrorMessage(e: unknown): string {
  if (e instanceof ApiClientError) {
    if (e.status === 401) return "Your session expired — please sign in again.";
    const code = (e.body as { error?: string } | null)?.error;
    if (code) return `Couldn't submit your request (${code}). Please try again.`;
  }
  return "Couldn't submit your request. Please try again.";
}
